
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Point, TowerType, EnemyType, GameState, Slot, TowerBranch, Mission, ThemeType } from './types';
import { 
  GAME_WIDTH, GAME_HEIGHT, MISSIONS, TOWER_STATS, THEME_DATA
} from './constants';
import { Enemy, Tower, Projectile, Particle } from './engine/GameEntities';

const App: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<GameState>('MENU');
  const [currentLevel, setCurrentLevel] = useState<Mission | null>(null);
  const [unlockedLevel, setUnlockedLevel] = useState(1);
  const [gold, setGold] = useState(500);
  const [lives, setLives] = useState(20);
  const [wave, setWave] = useState(0);
  const [gameSpeed, setGameSpeed] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [popups, setPopups] = useState<{id: number, x: number, y: number, text: string}[]>([]);
  const [waveTimer, setWaveTimer] = useState<number | null>(null);
  const [screenShake, setScreenShake] = useState(0);

  const enemiesRef = useRef<Enemy[]>([]);
  const towersRef = useRef<Tower[]>([]);
  const projectilesRef = useRef<Projectile[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const frameRef = useRef<number>(0);
  const popupIdCounter = useRef(0);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Persistence
  useEffect(() => {
    const saved = localStorage.getItem('unlockedLevel');
    if (saved) setUnlockedLevel(parseInt(saved));
  }, []);

  const playSound = (freq: number, type: OscillatorType = 'sine', duration: number = 0.1) => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = audioCtxRef.current.createOscillator();
    const gain = audioCtxRef.current.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, audioCtxRef.current.currentTime);
    gain.gain.setValueAtTime(0.1, audioCtxRef.current.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtxRef.current.currentTime + duration);
    osc.connect(gain);
    gain.connect(audioCtxRef.current.destination);
    osc.start();
    osc.stop(audioCtxRef.current.currentTime + duration);
  };

  const startLevel = (mission: Mission) => {
    setCurrentLevel(mission);
    setSlots(mission.slots);
    setGold(500 + mission.id * 50);
    setLives(20);
    setWave(0);
    enemiesRef.current = [];
    towersRef.current = [];
    projectilesRef.current = [];
    particlesRef.current = [];
    setGameState('PLAYING');
    playSound(440, 'triangle', 0.5);
  };

  const spawnWave = useCallback(() => {
    if (!currentLevel) return;
    setWaveTimer(null);
    const nextWave = wave + 1;
    setWave(nextWave);
    const types = [EnemyType.ORC, EnemyType.WOLF, EnemyType.SHAMAN];
    if (nextWave % 5 === 0) types.push(EnemyType.BOSS);
    const count = 5 + nextWave * 2;
    const mult = 1 + (currentLevel.id * 0.1) + (nextWave * 0.15);
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        if (gameState !== 'PLAYING') return;
        const type = types[Math.floor(Math.random() * Math.min(types.length, 1 + nextWave / 4))];
        enemiesRef.current.push(new Enemy(type, mult, currentLevel.path));
      }, i * (1000 / gameSpeed));
    }
  }, [wave, gameState, gameSpeed, currentLevel]);

  useEffect(() => {
    if (gameState === 'PLAYING' && enemiesRef.current.length === 0 && waveTimer === null) {
      if (wave === currentLevel?.waves) {
        setGameState('VICTORY');
        if (currentLevel.id === unlockedLevel) {
          const next = unlockedLevel + 1;
          setUnlockedLevel(next);
          localStorage.setItem('unlockedLevel', next.toString());
        }
      } else {
        setWaveTimer(5);
      }
    }
  }, [enemiesRef.current.length, gameState, waveTimer]);

  useEffect(() => {
    if (waveTimer !== null && waveTimer > 0) {
      const t = setTimeout(() => setWaveTimer(prev => prev! - 1), 1000);
      return () => clearTimeout(t);
    } else if (waveTimer === 0) {
      spawnWave();
    }
  }, [waveTimer, spawnWave]);

  useEffect(() => {
    if (gameState !== 'PLAYING' || !currentLevel) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const theme = THEME_DATA[currentLevel.theme];

    const render = () => {
      ctx.save();
      if (screenShake > 0) {
        ctx.translate((Math.random() - 0.5) * screenShake, (Math.random() - 0.5) * screenShake);
        setScreenShake(s => Math.max(0, s - 0.5));
      }

      ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
      ctx.strokeStyle = theme.pathEdge; ctx.lineWidth = 44; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(currentLevel.path[0].x, currentLevel.path[0].y); currentLevel.path.forEach(p => ctx.lineTo(p.x, p.y)); ctx.stroke();
      ctx.strokeStyle = theme.path; ctx.lineWidth = 36; ctx.stroke();

      slots.forEach(slot => {
        const built = towersRef.current.find(t => t.x === slot.x && t.y === slot.y);
        if (!built) {
          ctx.fillStyle = slot.isLocked ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.2)';
          ctx.beginPath(); ctx.arc(slot.x, slot.y, 22, 0, Math.PI*2); ctx.fill();
        }
      });

      enemiesRef.current = enemiesRef.current.filter(e => {
        e.update(gameSpeed);
        if (e.finished) { setLives(l => { if(l<=1) setGameState('GAMEOVER'); return l-1; }); return false; }
        if (e.isDead) { setGold(g => g + e.reward); playSound(220, 'sine', 0.05); return false; }
        e.draw(ctx); return true;
      });

      towersRef.current.forEach(t => {
        t.draw(ctx, selectedSlot?.x === t.x && selectedSlot?.y === t.y);
        const now = Date.now(); const { range, damage, cooldown, color } = t.stats;
        if (t.type !== TowerType.BARRACKS) {
          const targets = enemiesRef.current.filter(e => Math.sqrt((e.x-t.x)**2 + (e.y-t.y)**2) <= range).sort((a,b) => b.distanceTraveled - a.distanceTraveled);
          if (targets.length > 0 && now - t.lastShot >= (cooldown / gameSpeed)) {
            projectilesRef.current.push(new Projectile(t.x, t.y-40, targets[0], damage, color, t.type));
            t.lastShot = now; playSound(660, 'square', 0.05);
            if(t.type === TowerType.ARTILLERY) setScreenShake(4);
          }
        } else {
            t.soldiers.forEach(s => { s.update(gameSpeed); if (!s.blockingEnemy) {
              const ts = enemiesRef.current.find(e => !e.isBlocked && Math.sqrt((e.x-s.x)**2 + (e.y-s.y)**2) < 30);
              if (ts) { ts.isBlocked = true; s.blockingEnemy = ts; }
            } else if (now % Math.floor(40/gameSpeed) === 0) { s.blockingEnemy.takeDamage(damage); playSound(150, 'sawtooth', 0.02); }
          });
        }
      });

      projectilesRef.current = projectilesRef.current.filter(p => {
        p.update(gameSpeed, enemiesRef.current, (pts) => particlesRef.current.push(...pts)); p.draw(ctx); return !p.isExpired;
      });

      particlesRef.current = particlesRef.current.filter(p => { p.update(); p.draw(ctx); return p.life > 0; });

      ctx.restore();
      frameRef.current = requestAnimationFrame(render);
    };
    frameRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frameRef.current);
  }, [gameState, gameSpeed, selectedSlot, slots, currentLevel, screenShake]);

  const unlockSlot = (slot: Slot) => {
    if (gold >= slot.unlockCost) {
      setGold(g => g - slot.unlockCost);
      setSlots(prev => prev.map(s => s.x === slot.x && s.y === slot.y ? { ...s, isLocked: false } : s));
      setSelectedSlot(null); playSound(880, 'sine', 0.2);
    }
  };

  const buildOrUpgrade = (branch: TowerBranch = TowerBranch.NONE, type?: TowerType) => {
    if (!selectedSlot) return;
    const t = towersRef.current.find(it => it.x === selectedSlot.x && it.y === selectedSlot.y);
    if (t) {
      const cost = t.stats.upgradeCost;
      if (gold >= cost) { setGold(g => g - cost); t.upgrade(branch); setSelectedSlot(null); playSound(500, 'sine', 0.3); }
    } else if (type) {
      const cost = TOWER_STATS[type].cost;
      if (gold >= cost) { setGold(g => g - cost); towersRef.current.push(new Tower(type, selectedSlot.x, selectedSlot.y)); setSelectedSlot(null); playSound(400, 'triangle', 0.2); }
    }
  };

  return (
    <div className="relative w-screen h-screen flex items-center justify-center bg-[#1a110a] font-medieval overflow-hidden">
      {gameState === 'MENU' && (
        <div className="flex flex-col items-center bg-[#2c1e13] p-20 rounded-[4rem] border-[16px] border-[#d9c58c] shadow-2xl z-50">
          <h1 className="text-8xl text-[#ffd700] font-black mb-16 text-center drop-shadow-2xl">FREEDOM<br/>DEFENSE</h1>
          <button onClick={() => setGameState('CAMPAIGN')} className="py-6 px-20 bg-[#8B0000] border-8 border-[#ffd700] text-[#ffd700] text-5xl font-black rounded-3xl hover:scale-110 transition-transform shadow-2xl">CAMPAIGN</button>
        </div>
      )}

      {gameState === 'CAMPAIGN' && (
        <div className="flex flex-col items-center w-full max-w-5xl bg-[#2c1e13] p-10 rounded-3xl border-8 border-[#d9c58c] z-50 max-h-[90vh] overflow-y-auto custom-scrollbar">
          <h2 className="text-6xl text-[#ffd700] font-black mb-10">SELECT MISSION</h2>
          <div className="grid grid-cols-5 gap-6">
            {MISSIONS.map(m => (
              <button key={m.id} disabled={m.id > unlockedLevel} onClick={() => startLevel(m)} className={`w-20 h-20 rounded-2xl border-4 flex items-center justify-center text-3xl font-black transition-all ${m.id <= unlockedLevel ? 'bg-[#4d7c2c] border-[#ffd700] text-white hover:scale-110' : 'bg-gray-800 border-gray-600 text-gray-500 cursor-not-allowed'}`}>
                {m.id}
              </button>
            ))}
          </div>
          <button onClick={() => setGameState('MENU')} className="mt-10 text-[#ffd700] text-2xl hover:underline">BACK TO MENU</button>
        </div>
      )}

      {gameState === 'PLAYING' && (
        <div className="relative shadow-2xl border-[12px] border-[#3d2b1f] rounded-2xl overflow-hidden bg-black">
          <canvas ref={canvasRef} width={GAME_WIDTH} height={GAME_HEIGHT} onClick={(e) => {
            const rect = canvasRef.current?.getBoundingClientRect(); if (!rect) return;
            const x = (e.clientX - rect.left) * (GAME_WIDTH / rect.width);
            const y = (e.clientY - rect.top) * (GAME_HEIGHT / rect.height);
            const slot = slots.find(s => Math.sqrt((s.x-x)**2 + (s.y-y)**2) < 30);
            setSelectedSlot(slot || null);
          }} />
          
          <div className="absolute top-0 left-0 w-full p-6 flex justify-between pointer-events-none z-40">
            <div className="flex gap-4">
              <div className="bg-[#2c1e13]/90 border-4 border-[#d9c58c] px-6 py-2 rounded-2xl text-[#d9c58c] flex items-center gap-3">
                <span className="text-2xl">❤️</span> <span className="text-3xl font-black">{lives}</span>
              </div>
              <div className="bg-[#2c1e13]/90 border-4 border-[#d9c58c] px-6 py-2 rounded-2xl text-[#ffd700] flex items-center gap-3">
                <span className="text-2xl">💰</span> <span className="text-3xl font-black">{gold}</span>
              </div>
            </div>
            <div className="bg-[#2c1e13]/90 border-4 border-[#d9c58c] px-6 py-2 rounded-2xl text-[#ffd700] flex items-center gap-3 pointer-events-auto">
                <button onClick={() => setGameSpeed(1)} className={`px-4 py-1 rounded-lg ${gameSpeed === 1 ? 'bg-[#ffd700] text-black' : 'text-white'}`}>1x</button>
                <button onClick={() => setGameSpeed(2)} className={`px-4 py-1 rounded-lg ${gameSpeed === 2 ? 'bg-[#ffd700] text-black' : 'text-white'}`}>2x</button>
            </div>
          </div>

          {waveTimer !== null && waveTimer > 0 && (
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 text-9xl text-[#ffd700] font-black animate-ping opacity-50">{waveTimer}</div>
          )}

          <div className="absolute bottom-8 right-8 pointer-events-auto">
            <button onClick={spawnWave} className="w-24 h-24 bg-[#8B0000] border-8 border-[#ffd700] rounded-full text-5xl hover:scale-110 active:scale-95 transition-all shadow-2xl">⚔️</button>
          </div>

          {selectedSlot && (
            <div className="absolute z-50 p-6 bg-[#2c1e13]/95 border-[6px] border-[#d9c58c] rounded-3xl shadow-2xl flex gap-6 animate-bounce"
                 style={{ left: selectedSlot.x, top: selectedSlot.y, transform: 'translate(-50%, -130%)' }}>
              {selectedSlot.isLocked ? (
                <button onClick={() => unlockSlot(selectedSlot)} className="bg-[#4d7c2c] px-8 py-4 rounded-xl text-white font-black hover:bg-[#5a8c3e]">UNLOCK {selectedSlot.unlockCost} 💰</button>
              ) : towersRef.current.find(t => t.x === selectedSlot.x && t.y === selectedSlot.y) ? (
                (() => {
                  const t = towersRef.current.find(it => it.x === selectedSlot.x && it.y === selectedSlot.y)!;
                  if (t.level === 4 && t.branch === TowerBranch.NONE) return (
                    <div className="flex gap-4">
                      <button onClick={() => buildOrUpgrade(TowerBranch.PATH_A)} className="bg-[#4d7c2c] p-4 rounded-xl text-white font-bold">PATH A: POWER</button>
                      <button onClick={() => buildOrUpgrade(TowerBranch.PATH_B)} className="bg-[#8B0000] p-4 rounded-xl text-white font-bold">PATH B: SPEED</button>
                    </div>
                  );
                  if (t.level < 5) return <button onClick={() => buildOrUpgrade()} className="bg-[#4d7c2c] p-4 rounded-xl text-white font-bold">UPGRADE {t.stats.upgradeCost} 💰</button>;
                  return <span className="text-[#ffd700] font-bold p-4">MAX LEVEL</span>;
                })()
              ) : (
                <div className="flex gap-4">
                  {Object.values(TowerType).map(type => (
                    <button key={type} onClick={() => buildOrUpgrade(TowerBranch.NONE, type)} className={`flex flex-col items-center gap-1 ${gold < TOWER_STATS[type].cost ? 'opacity-40' : 'hover:scale-110 transition-transform'}`}>
                      <div className="w-16 h-16 bg-[#3d2b1f] border-4 border-[#d9c58c] rounded-xl flex items-center justify-center text-3xl">
                        {type === TowerType.ARCHER ? '🏹' : type === TowerType.BARRACKS ? '🛡️' : type === TowerType.MAGE ? '🔮' : '💣'}
                      </div>
                      <span className="text-[#ffd700] font-black">{TOWER_STATS[type].cost}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {gameState === 'VICTORY' && (
        <div className="fixed inset-0 bg-black/80 flex flex-col items-center justify-center z-[200]">
          <h2 className="text-9xl text-[#ffd700] font-black mb-8 animate-bounce">VICTORY</h2>
          <p className="text-3xl text-white mb-12">MISSION {currentLevel?.id} COMPLETED!</p>
          <button onClick={() => setGameState('CAMPAIGN')} className="px-16 py-6 bg-[#4d7c2c] border-8 border-[#ffd700] text-white text-3xl font-black rounded-2xl hover:scale-105 transition-all shadow-2xl">CONTINUE</button>
        </div>
      )}

      {gameState === 'GAMEOVER' && (
        <div className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-[200]">
          <h2 className="text-9xl text-red-600 font-black mb-8">DEFEAT</h2>
          <button onClick={() => setGameState('CAMPAIGN')} className="px-16 py-6 bg-[#8B0000] border-8 border-[#ffd700] text-[#ffd700] text-3xl font-black rounded-2xl hover:scale-105 transition-all">RETRY</button>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 12px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #1a110a; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d9c58c; border-radius: 6px; }
      `}</style>
    </div>
  );
};

export default App;